import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import Spinner from '../Atoms/Spinner';
import { useTranslation } from 'react-i18next';

const UploadContainer = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 15px;
`;

const RequiredMark = styled.span`
  color: #dc3545;
  margin-left: 4px;
`;

const DropzoneArea = styled.div`
  border: 2px dashed ${props => props.isDragActive ? '#0000FF' : '#e0e0e0'};
  border-radius: 8px;
  padding: ${props => props.hasFiles ? '20px' : '40px'};
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${props => props.isDragActive ? 'rgba(0, 0, 255, 0.05)' : '#fafafa'};
  
  &:hover {
    border-color: #0000FF;
    background-color: rgba(0, 0, 255, 0.05);
  }
`;

const UploadIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 15px;
  background: linear-gradient(135deg, #0000FF 0%, #0000CC 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: bold;
`;

const UploadText = styled.div`
  font-size: 16px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
`;

const UploadSubText = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
`;

const AcceptedFormats = styled.div`
  font-size: 12px;
  color: #999;
`;

const FilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 20px;
`;

const FileCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const FilePreview = styled.div`
  width: 100%;
  height: 150px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FileTypeIcon = styled.div`
  font-size: 48px;
  color: #666;
`;

const FileInfo = styled.div`
  padding: 10px;
`;

const FileName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 5px;
`;

const FileSize = styled.div`
  font-size: 12px;
  color: #666;
`;

const CreditLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  margin-top: 8px;
  text-align: center;
`;

const CreditInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 12px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0000FF;
    box-shadow: 0 0 0 2px rgba(0, 0, 255, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 18px;
  line-height: 1;
  z-index: 10;

  &:hover {
    background: rgba(220, 53, 69, 1);
    transform: scale(1.1);
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 13px;
  margin-top: 5px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
`;

const PieceMediaUpload = ({ 
  value = [], 
  onChange, 
  maxSizeMB = 50,
  error,
  pieceIndex 
}) => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadErrors, setUploadErrors] = useState({});
  const [uploading, setUploading] = useState({});
  const fileInputRef = useRef(null);

  const acceptedFormats = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'video/mp4': ['.mp4'],
    'video/quicktime': ['.mov'],
    'application/pdf': ['.pdf']
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, [value]);

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    files.forEach((file, index) => {
      const fileId = `${Date.now()}_${index}`;
      
      // Validate file type
      if (!Object.keys(acceptedFormats).includes(file.type)) {
        setUploadErrors(prev => ({
          ...prev,
          [fileId]: 'Format de fichier non supporté'
        }));
        return;
      }
      
      // Validate file size
      if (file.size > maxSizeMB * 1024 * 1024) {
        setUploadErrors(prev => ({
          ...prev,
          [fileId]: `Le fichier doit faire moins de ${maxSizeMB}MB`
        }));
        return;
      }
      
      setUploading(prev => ({ ...prev, [fileId]: true }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const newFile = {
          id: fileId,
          file: file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: e.target.result,
          credit: ''
        };
        
        onChange([...value, newFile]);
        setUploading(prev => {
          const updated = { ...prev };
          delete updated[fileId];
          return updated;
        });
      };
      
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        reader.readAsDataURL(file);
      } else {
        // For PDFs, we don't need a preview
        const newFile = {
          id: fileId,
          file: file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: null,
          credit: ''
        };
        
        onChange([...value, newFile]);
        setUploading(prev => {
          const updated = { ...prev };
          delete updated[fileId];
          return updated;
        });
      }
    });
  };

  const removeFile = (fileId) => {
    onChange(value.filter(f => f.id !== fileId));
    setUploadErrors(prev => {
      const updated = { ...prev };
      delete updated[fileId];
      return updated;
    });
  };

  const updateFileCredit = (fileId, credit) => {
    onChange(value.map(f => 
      f.id === fileId ? { ...f, credit } : f
    ));
  };

  const renderPreview = (file) => {
    if (file.type.startsWith('image/')) {
      return <img src={file.preview} alt={file.name} />;
    } else if (file.type.startsWith('video/')) {
      return <video src={file.preview} muted />;
    } else if (file.type === 'application/pdf') {
      return <FileTypeIcon>📄</FileTypeIcon>;
    }
    return <FileTypeIcon>📎</FileTypeIcon>;
  };

  return (
    <UploadContainer>
      <Label>
        {isEn 
          ? 'Piece visuals (photos, videos, sketches, 3D)'
          : 'Visuels de la pièce (photos, vidéos, croquis, 3D)'}
        <RequiredMark>*</RequiredMark>
        {value.length > 0 && ` (${value.length})`}
      </Label>
      
      <DropzoneArea
          isDragActive={isDragActive}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          hasFiles={value.length > 0}
        >
          {!value.length && (
            <>
              <UploadIcon>+</UploadIcon>
              <UploadText>
                {isEn 
                  ? 'Drag files here or click to select'
                  : 'Glissez vos fichiers ici ou cliquez pour sélectionner'}
              </UploadText>
              <UploadSubText>
                {isEn 
                  ? `Maximum ${maxSizeMB}MB per file`
                  : `Maximum ${maxSizeMB}MB par fichier`}
              </UploadSubText>
              <AcceptedFormats>
                {isEn 
                  ? 'Accepted formats: JPG, PNG, WebP, MP4, MOV, PDF'
                  : 'Formats acceptés: JPG, PNG, WebP, MP4, MOV, PDF'}
              </AcceptedFormats>
            </>
          )}
          {value.length > 0 && (
            <UploadText>
              {isEn ? '+ Add more files' : '+ Ajouter d\'autres fichiers'}
            </UploadText>
          )}
        </DropzoneArea>
      
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={Object.values(acceptedFormats).flat().join(',')}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      
      {value.length > 0 && (
        <FilesGrid>
          {value.map(file => (
            <FileCard key={file.id}>
              {uploading[file.id] && (
                <LoadingOverlay>
                  <Spinner />
                </LoadingOverlay>
              )}
              <FilePreview>
                {renderPreview(file)}
                <RemoveButton
                  type="button"
                  onClick={() => removeFile(file.id)}
                >
                  ×
                </RemoveButton>
              </FilePreview>
              <FileInfo>
                <FileName>{file.name}</FileName>
                <FileSize>{formatFileSize(file.size)}</FileSize>
                <CreditLabel>
                  {isEn ? 'Photo/video credit' : 'Crédit photo/vidéo'}
                  <RequiredMark>*</RequiredMark>
                </CreditLabel>
                <CreditInput
                  type="text"
                  value={file.credit || ''}
                  onChange={(e) => updateFileCredit(file.id, e.target.value)}
                  placeholder={isEn ? '© Photo/video credit' : '© Crédit photo/vidéo'}
                  onClick={(e) => e.stopPropagation()}
                />
              </FileInfo>
              {uploadErrors[file.id] && (
                <ErrorMessage>{uploadErrors[file.id]}</ErrorMessage>
              )}
            </FileCard>
          ))}
        </FilesGrid>
      )}
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </UploadContainer>
  );
};

export default PieceMediaUpload;